import React, { useEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { DeudaMensual } from '@/services/debtService';
import type { ConceptoDeDeuda } from '@/services/debtConceptService';
import { toast } from 'sonner';
import { MinusCircle } from 'lucide-react';

interface DebtFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (debt: DeudaMensual, conceptos: ConceptoDeDeuda[]) => void;
  initialData?: DeudaMensual | null;
  initialConcepts?: ConceptoDeDeuda[];
  inquilinoId: string;
  edificioId: string;
}

const conceptoSchema = z.object({
  tipo: z.string().min(1, { message: "El tipo de concepto es requerido." }),
  descripcion: z.string().optional(),
  monto: z.coerce.number<number>().positive({ message: "El monto debe ser un número positivo." }),
});

const formSchema = z.object({
  mes_correspondiente: z.string().min(1, { message: "El mes correspondiente es requerido." }),
  fecha_vencimiento: z.string().min(1, { message: "La fecha de vencimiento es requerida." }),
  conceptos: z.array(conceptoSchema).min(1, { message: "Debe haber al menos un concepto de deuda." }),
});

const DebtForm: React.FC<DebtFormProps> = ({ isOpen, onClose, onSave, initialData, initialConcepts, inquilinoId, edificioId }) => {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      mes_correspondiente: new Date().toISOString().split('T')[0].substring(0, 7), // YYYY-MM format
      fecha_vencimiento: new Date().toISOString().split('T')[0],
      conceptos: initialConcepts && initialConcepts.length > 0 ? initialConcepts : [
        { tipo: "Renta Fija", descripcion: "Cuota mensual de alquiler", monto: 0 },
        { tipo: "Mantenimiento", descripcion: "Cuota de mantenimiento de áreas comunes", monto: 0 }
      ],
    }
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "conceptos",
  });

  useEffect(() => {
    if (initialData) {
      form.reset({
        mes_correspondiente: initialData.mes_correspondiente.substring(0, 7) || "",
        fecha_vencimiento: initialData.fecha_vencimiento || "",
        conceptos: initialConcepts && initialConcepts.length > 0 ? initialConcepts : [
          { tipo: "Renta Fija", descripcion: "Cuota mensual de alquiler", monto: 0 },
          { tipo: "Mantenimiento", descripcion: "Cuota de mantenimiento de áreas comunes", monto: 0 }
        ],
      });
    } else {
      form.reset();
    }
  }, [initialData, initialConcepts, form]);

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    const montoTotalAdeudado = values.conceptos.reduce((sum, concepto) => sum + concepto.monto, 0);

    const deudaId = initialData?.id_deuda || '';
    onSave({
      ...initialData,
      id_deuda: deudaId,
      id_inquilino: inquilinoId,
      id_edificio: edificioId,
      mes_correspondiente: values.mes_correspondiente + '-01', // Convert to YYYY-MM-DD
      monto_total_adeudado: montoTotalAdeudado,
      fecha_vencimiento: values.fecha_vencimiento,
      esta_pagada: initialData?.esta_pagada || false,
      saldo_pendiente: initialData?.saldo_pendiente || montoTotalAdeudado,
    }, values.conceptos.map(concepto => ({
      ...concepto,
      id_deuda: deudaId
    })));
    onClose();
    toast.success(initialData ? "Deuda actualizada exitosamente." : "Deuda creada exitosamente.");
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{initialData ? "Editar Deuda" : "Generar Nueva Deuda"}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="mes_correspondiente"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Mes Correspondiente (YYYY-MM)</FormLabel>
                  <FormControl>
                    <Input type="month" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="fecha_vencimiento"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Fecha de Vencimiento</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <h3 className="text-lg font-semibold mt-6 mb-2">Conceptos de Deuda</h3>
            {fields.map((field, index) => (
              <div key={field.id} className="flex space-x-2 items-end">
                <FormField
                  control={form.control}
                  name={`conceptos.${index}.tipo`}
                  render={({ field: conceptField }) => (
                    <FormItem className="flex-1">
                      <FormLabel>Tipo</FormLabel>
                      <Select onValueChange={conceptField.onChange} defaultValue={conceptField.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecciona un tipo" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="Renta Fija">Renta Fija</SelectItem>
                          <SelectItem value="Mantenimiento">Mantenimiento</SelectItem>
                          <SelectItem value="Multa">Multa</SelectItem>
                          <SelectItem value="Consumo Agua Extra">Consumo Agua Extra</SelectItem>
                          <SelectItem value="Garaje">Garaje</SelectItem>
                          <SelectItem value="Otro">Otro</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name={`conceptos.${index}.descripcion`}
                  render={({ field: conceptField }) => (
                    <FormItem className="flex-1">
                      <FormLabel>Descripción</FormLabel>
                      <FormControl>
                        <Input placeholder="Descripción del concepto" {...conceptField} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name={`conceptos.${index}.monto`}
                  render={({ field: conceptField }) => (
                    <FormItem className="w-24">
                      <FormLabel>Monto</FormLabel>
                      <FormControl>
                        <Input type="number" step="0.01" placeholder="0.00" {...conceptField} onChange={e => conceptField.onChange(Number(e.target.value))} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button type="button" variant="outline" size="icon" onClick={() => remove(index)}>
                  <MinusCircle className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => append({ tipo: "Otro", descripcion: "", monto: 0 })}
            >
              Agregar Concepto
            </Button>

            <Button type="submit" className="w-full">Guardar Deuda</Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default DebtForm;
